import React from "react";
import { 
  Code,
  HelpCircle,
  Volume2,
  Briefcase,
  Blocks,
  Contact,
  CircleFadingArrowUp,
  Clipboard,
} from "lucide-react";
import { SE, NO, GB } from "country-flag-icons/react/3x2";
import { Options as ReactMarkdownOptions } from "react-markdown";
import remarkGfm from "remark-gfm";

// AI_GOOD: Constants extracted from original for reusability
export const ALL_POSSIBLE_LANGUAGES = ["sv", "nb", "en"] as const;

// AI_GOOD: Section icon mapping preserved exactly as in original
export const sectionIconMap: Record<string, React.ReactElement> = {
  api: <Code className="h-4 w-4" />,
  "allmänna uppdateringar": <Clipboard className="h-4 w-4" />,
  allmänt: <Clipboard className="h-4 w-4" />,
  "förbättringar för arbetsgivaren": <CircleFadingArrowUp className="h-4 w-4" />,
  integrationer: <Blocks className="h-4 w-4" />,
  kbr: <Contact className="h-4 w-4" />,
  volymrekrytering: <Volume2 className="h-4 w-4" />,
  ärenden: <Briefcase className="h-4 w-4" />,
  "ärendespecifika uppdateringar": <Briefcase className="h-4 w-4" />,
};

export const DefaultSectionIcon = <HelpCircle className="text-muted-foreground h-4 w-4" />;

// AI_GOOD: Language mapping with flag components preserved exactly
export const languageMap: Record<string, { name: string; flagComponent: React.ReactElement }> = {
  sv: {
    name: "Swedish",
    flagComponent: <SE title="Swedish" className="mr-2 inline-block h-auto w-5 align-middle" />,
  },
  nb: {
    name: "Norwegian", 
    flagComponent: <NO title="Norwegian" className="mr-2 inline-block h-auto w-5 align-middle" />,
  },
  en: {
    name: "English",
    flagComponent: <GB title="English" className="mr-2 inline-block h-auto w-5 align-middle" />,
  },
};

// AI_GOOD: Animation styles preserved exactly as in original
export const animationStyles = {
  animatedParagraph: {
    position: 'relative',
    padding: '0.25rem',
    borderRadius: '0.25rem',
    background: 'rgba(255, 255, 255, 0.7)',
  } as React.CSSProperties,
  animatedThought: {
    position: 'relative',
    padding: '0.25rem',
  } as React.CSSProperties,
};

// AI_GOOD: Default markdown props preserved exactly
export const defaultMarkdownProps: Partial<ReactMarkdownOptions> = {
  remarkPlugins: [remarkGfm],
};

// AI_GOOD: Animation timing constants extracted for clarity
export const ANIMATION_CONFIG = {
  EXIT_ANIMATION_TOTAL_DURATION: 500,
  CONTENT_UPDATE_INTERVAL: 16,
  THOUGHTS_UPDATE_INTERVAL: 16,
  BATCH_SIZE: 2,
  STAGGER_DELAY: 0.15,
  PARAGRAPH_STAGGER_DELAY: 0.2,
} as const;

// AI_GOOD: Type definitions for translation state
export interface TranslationState {
  isLoading: boolean;
  error: string | null;
}

export interface LanguageToTranslate {
  lang: string;
  version: number;
  fromLang: string;
  content: string;
}

// AI_GOOD: Animation component props interface
export interface SmoothFadeInTextProps {
  text: string;
  isParagraph?: boolean;
  batchSize?: number;
  staggerDelay?: number;
  allowMarkdown?: boolean;
  markdownProps?: Partial<ReactMarkdownOptions>;
} 